<script setup lang="ts" generic="T extends Record<string, any>">
import type { BulletLegendItemInterface } from '@unovis/ts'
import { Axis, GroupedBar, StackedBar } from '@unovis/ts'
import type { BaseChartProps } from '.'
import { cn } from '@/lib/utils'
import { ChartCrosshair, ChartLegend, defaultColors } from '@/components/ui/chart'
import { VisAxis, VisGroupedBar, VisStackedBar, VisXYContainer } from '@unovis/vue'
import { useMounted } from '@vueuse/core'
import { type Component, computed, ref } from 'vue'
import Skeleton from '@/components/ui/skeleton/Skeleton.vue'

const props = withDefaults(
  defineProps<
    BaseChartProps<T> & {
      customTooltip?: Component
      type?: 'stacked' | 'grouped'
      roundedCorners?: number
      formatCell?: (field: string, value: any) => any
    }
  >(),
  {
    data: () => [],
    categories: () => [],
    index: '' as KeyOfT,
    type: 'grouped',
    margin: () => ({ top: 25, bottom: 25, left: 0, right: 0 }),
    filterOpacity: 0.2,
    roundedCorners: 0,
    showXAxis: true,
    showYAxis: true,
    showTooltip: true,
    showLegend: true,
    showGridLine: true,
  },
)
const emits = defineEmits<{
  legendItemClick: [d: BulletLegendItemInterface, i: number]
}>()

type KeyOfT = Extract<keyof T, string>
type Data = (typeof props.data)[number]

const index = computed(() => props.index as KeyOfT)
const colors = computed(() =>
  props.colors?.length ? props.colors : defaultColors(props.categories.length),
)
const legendItems = ref<BulletLegendItemInterface[]>(
  props.categories.map((category, i) => ({
    name: category,
    color: colors.value[i],
    inactive: false,
  })),
)

const isMounted = useMounted()

function handleLegendItemClick(d: BulletLegendItemInterface, i: number) {
  emits('legendItemClick', d, i)
}

const VisBarComponent = computed(() => (props.type === 'grouped' ? VisGroupedBar : VisStackedBar))
const selectorsBar = computed(() =>
  props.type === 'grouped' ? GroupedBar.selectors.bar : StackedBar.selectors.bar,
)
</script>

<template>
  <div v-if="props.data.length">
    <div :class="cn('w-full h-[400px] flex flex-col items-end', $attrs.class ?? '')">
      <ChartLegend
        v-if="props.showLegend"
        v-model:items="legendItems"
        @legend-item-click="handleLegendItemClick"
      />

      <VisXYContainer
        :data="props.data"
        :style="{ height: isMounted ? '100%' : 'auto' }"
        :margin="props.margin"
      >
        <ChartCrosshair
          v-if="props.showTooltip"
          :colors="colors"
          :items="legendItems"
          :custom-tooltip="props.customTooltip"
          :index="props.index"
          :format-cell="props.formatCell"
        />

        <VisBarComponent
          :x="(d: Data, i: number) => i"
          :y="props.categories.map((category) => (d: Data) => d[category])"
          :color="colors"
          :rounded-corners="props.roundedCorners"
          :bar-padding="0.05"
          :attributes="{
            [selectorsBar]: {
              opacity: (d: Data, i: number) => {
                const pos = i % props.categories.length
                return legendItems[pos]?.inactive ? props.filterOpacity : 1
              },
            },
          }"
        />

        <VisAxis
          v-if="props.showXAxis"
          type="x"
          :tick-format="props.xFormatter ?? ((v: number) => props.data[v]?.[index])"
          :grid-line="false"
          :tick-line="false"
          tick-text-color="hsl(var(--vis-text-color))"
        />
        <VisAxis
          v-if="props.showYAxis"
          type="y"
          :tick-line="false"
          :tick-format="props.yFormatter"
          :domain-line="false"
          :grid-line="props.showGridLine"
          :attributes="{
            [Axis.selectors.grid]: {
              class: 'text-muted',
            },
          }"
          tick-text-color="hsl(var(--vis-text-color))"
        />

        <slot />
      </VisXYContainer>
    </div>
  </div>
  <div v-else>
    <div class="flex items-end gap-10">
      <Skeleton class="h-96 w-[120px]" />
      <Skeleton class="h-48 w-[120px]" />
      <Skeleton class="h-80 w-[120px]" />
      <Skeleton class="h-64 w-[120px]" />
      <Skeleton class="h-80 w-[120px]" />
      <Skeleton class="h-48 w-[120px]" />
      <Skeleton class="h-80 w-[120px]" />
      <Skeleton class="h-64 w-[120px]" />
      <Skeleton class="h-48 w-[120px]" />
      <Skeleton class="h-80 w-[120px]" />
    </div>
  </div>
</template>
